import { useState, useEffect } from 'react'
import { Routes, Route, Link, Navigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../contexts/AuthContext'
import {
  LayoutDashboard,
  Users,
  GraduationCap,
  BookOpen,
  MessageSquare,
  Bell,
  Settings,
  LogOut,
  Menu,
  X,
  Plus,
  DollarSign,
  Search,
  TrendingUp,
  School
} from 'lucide-react'
import supabase from '../../services/supabaseClient'
import LanguageSelector from '../../components/LanguageSelector'

export default function SchoolAdminDashboard() {
  const { t } = useTranslation()
  const { profile, signOut } = useAuth()
  const location = useLocation()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [stats, setStats] = useState({
    totalTeachers: 0,
    totalStudents: 0,
    totalParents: 0,
    totalClasses: 0
  })

  useEffect(() => {
    if (profile?.school_id) {
      fetchSchoolStats()
    }
  }, [profile])

  async function fetchSchoolStats() {
    try {
      const schoolId = profile.school_id

      const { count: teachersCount } = await supabase
        .from('users')
        .select('*', { count: 'exact', head: true })
        .eq('school_id', schoolId)
        .eq('role', 'teacher')

      const { count: studentsCount } = await supabase
        .from('learners')
        .select('*', { count: 'exact', head: true })
        .eq('school_id', schoolId)

      const { count: parentsCount } = await supabase
        .from('users')
        .select('*', { count: 'exact', head: true })
        .eq('school_id', schoolId)
        .eq('role', 'parent')

      const { count: classesCount } = await supabase
        .from('classes')
        .select('*', { count: 'exact', head: true })
        .eq('school_id', schoolId)

      setStats({
        totalTeachers: teachersCount || 0,
        totalStudents: studentsCount || 0,
        totalParents: parentsCount || 0,
        totalClasses: classesCount || 0
      })
    } catch (error) {
      console.error('Error fetching stats:', error)
    }
  }

  const navigation = [
    { name: t('common.dashboard'), href: '/admin/school', icon: LayoutDashboard },
    { name: t('roles.teacher') + 's', href: '/admin/school/teachers', icon: Users },
    { name: t('common.classes'), href: '/admin/school/classes', icon: BookOpen },
    { name: t('roles.student') + 's', href: '/admin/school/learners', icon: GraduationCap },
    { name: t('roles.parent') + 's', href: '/admin/school/parents', icon: Users },
    { name: t('common.announcements'), href: '/admin/school/announcements', icon: Bell },
    { name: t('common.messages'), href: '/admin/school/messages', icon: MessageSquare },
    { name: t('dashboard.billing'), href: '/admin/school/payments', icon: DollarSign },
    { name: t('common.settings'), href: '/admin/school/settings', icon: Settings },
  ]

  const isActive = (href) => location.pathname === href

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm" onClick={() => setSidebarOpen(false)} />
        <div className="fixed inset-y-0 left-0 flex w-72 flex-col bg-white border-r border-gray-100">
          <div className="flex h-20 items-center justify-between px-6 border-b border-gray-100">
            <div className="flex items-center">
              <div className="h-10 w-10 rounded-xl gradient-primary flex items-center justify-center text-white font-bold shadow-glow">
                S
              </div>
              <span className="ml-3 text-lg font-bold text-text-primary">{t('roles.schoolAdmin')}</span>
            </div>
            <button onClick={() => setSidebarOpen(false)} className="p-2 rounded-lg hover:bg-gray-100 text-text-secondary">
              <X className="h-5 w-5" />
            </button>
          </div>
          <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`nav-link ${isActive(item.href) ? 'active' : ''}`}
                onClick={() => setSidebarOpen(false)}
              >
                <item.icon className={`mr-3 h-5 w-5 ${isActive(item.href) ? 'text-primary-500' : 'text-gray-400'}`} />
                {item.name}
              </Link>
            ))}
          </nav>
          <div className="p-4 border-t border-gray-100">
            <button
              onClick={signOut}
              className="flex items-center w-full px-4 py-3 text-sm font-medium text-text-secondary rounded-xl hover:bg-gray-100 hover:text-text-primary transition-colors"
            >
              <LogOut className="mr-3 h-5 w-5 text-gray-400" />
              {t('auth.signOut')}
            </button>
          </div>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-72 lg:flex-col">
        <div className="flex flex-col flex-1 min-h-0 bg-white border-r border-gray-100">
          <div className="flex h-20 items-center px-6 border-b border-gray-100">
            <div className="h-10 w-10 rounded-xl gradient-primary flex items-center justify-center text-white font-bold shadow-glow">
              S
            </div>
            <span className="ml-3 text-lg font-bold text-text-primary">{t('roles.schoolAdmin')}</span>
          </div>
          <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`nav-link ${isActive(item.href) ? 'active' : ''}`}
              >
                <item.icon className={`mr-3 h-5 w-5 ${isActive(item.href) ? 'text-primary-500' : 'text-gray-400'}`} />
                {item.name}
              </Link>
            ))}
          </nav>
          <div className="p-4 border-t border-gray-100">
            <button
              onClick={signOut}
              className="flex items-center w-full px-4 py-3 text-sm font-medium text-text-secondary rounded-xl hover:bg-gray-100 hover:text-text-primary transition-colors"
            >
              <LogOut className="mr-3 h-5 w-5 text-gray-400" />
              {t('auth.signOut')}
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-72">
        {/* Top header */}
        <header className="dashboard-header sticky top-0 z-30">
          <div className="flex h-20 items-center justify-between px-4 sm:px-6 lg:px-8">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 rounded-lg text-text-secondary hover:bg-gray-100"
            >
              <Menu className="h-6 w-6" />
            </button>
            
            {/* Search */}
            <div className="hidden md:flex flex-1 max-w-md ml-8">
              <div className="relative w-full">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder={t('common.search')}
                  className="w-full pl-12 pr-4 py-3 rounded-xl bg-white border border-gray-200 text-text-primary placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-100 transition-all"
                />
              </div>
            </div>
            
            <div className="flex items-center ml-auto space-x-4">
              <LanguageSelector size="small" />
              <Link
                to="/admin/school/announcements/new"
                className="hidden sm:flex items-center px-4 py-2.5 text-sm font-semibold text-primary-600 bg-card-teal rounded-xl border border-primary-200 hover:bg-primary-50 transition-colors"
              >
                <Plus className="h-4 w-4 mr-1.5" />
                {t('announcements.newAnnouncement')}
              </Link>
              <button className="p-3 rounded-xl text-text-secondary hover:text-text-primary hover:bg-gray-100 transition-colors relative">
                <Bell className="h-5 w-5" />
                <span className="absolute top-2 right-2 h-2.5 w-2.5 bg-primary-500 rounded-full border-2 border-white"></span>
              </button>
              <div className="flex items-center pl-4 border-l border-gray-100">
                <div className="h-10 w-10 rounded-xl gradient-primary flex items-center justify-center text-white font-semibold shadow-glow">
                  {profile?.first_name?.[0]}{profile?.last_name?.[0]}
                </div>
                <div className="ml-3 hidden sm:block">
                  <p className="text-sm font-semibold text-text-primary">{profile?.first_name} {profile?.last_name}</p>
                  <p className="text-xs text-text-secondary">{t('roles.schoolAdmin')}</p>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="py-8 px-4 sm:px-6 lg:px-8">
          <Routes>
            <Route
              path="/"
              element={
                <div className="space-y-8 animate-fade-in">
                  {/* Page Header */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                      <h1 className="text-2xl font-bold text-text-primary font-heading">{t('common.dashboard')}</h1>
                      <p className="mt-1 text-sm text-text-secondary">
                        School Code: <span className="font-mono font-semibold text-primary-600">{profile?.schools?.code}</span>
                      </p>
                    </div>
                  </div>
                  
                  {/* Stats Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div className="stat-card hover-lift">
                      <div className="flex items-center">
                        <div className="stat-card-icon primary">
                          <Users className="h-6 w-6" />
                        </div>
                        <div className="ml-4">
                          <p className="text-sm font-medium text-text-secondary">{t('dashboard.totalTeachers')}</p>
                          <p className="text-2xl font-bold text-text-primary">{stats.totalTeachers}</p>
                        </div>
                      </div>
                    </div>

                    <div className="stat-card hover-lift">
                      <div className="flex items-center">
                        <div className="stat-card-icon success">
                          <GraduationCap className="h-6 w-6" />
                        </div>
                        <div className="ml-4">
                          <p className="text-sm font-medium text-text-secondary">{t('dashboard.totalStudents')}</p>
                          <p className="text-2xl font-bold text-text-primary">{stats.totalStudents}</p>
                        </div>
                      </div>
                    </div>

                    <div className="stat-card hover-lift">
                      <div className="flex items-center">
                        <div className="stat-card-icon warning">
                          <Users className="h-6 w-6" />
                        </div>
                        <div className="ml-4">
                          <p className="text-sm font-medium text-text-secondary">{t('dashboard.totalParents')}</p>
                          <p className="text-2xl font-bold text-text-primary">{stats.totalParents}</p>
                        </div>
                      </div>
                    </div>

                    <div className="stat-card hover-lift">
                      <div className="flex items-center">
                        <div className="stat-card-icon secondary">
                          <BookOpen className="h-6 w-6" />
                        </div>
                        <div className="ml-4">
                          <p className="text-sm font-medium text-text-secondary">{t('dashboard.totalClasses')}</p>
                          <p className="text-2xl font-bold text-text-primary">{stats.totalClasses}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Quick Actions & Billing */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="card p-6">
                      <h2 className="text-lg font-bold text-text-primary mb-6 font-heading">{t('dashboard.quickActions')}</h2>
                      <div className="grid grid-cols-2 gap-4">
                        <Link
                          to="/admin/school/teachers"
                          className="flex items-center p-4 border border-gray-200 rounded-xl hover:border-primary-300 hover:bg-card-teal transition-all group"
                        >
                          <div className="p-2 rounded-lg bg-card-teal text-primary-500 group-hover:bg-primary-100 transition-colors">
                            <Plus className="h-5 w-5" />
                          </div>
                          <span className="ml-3 text-sm font-semibold text-text-secondary">{t('common.add')} {t('roles.teacher')}</span>
                        </Link>
                        <Link
                          to="/admin/school/classes"
                          className="flex items-center p-4 border border-gray-200 rounded-xl hover:border-primary-300 hover:bg-card-teal transition-all group"
                        >
                          <div className="p-2 rounded-lg bg-card-teal text-primary-500 group-hover:bg-primary-100 transition-colors">
                            <Plus className="h-5 w-5" />
                          </div>
                          <span className="ml-3 text-sm font-semibold text-text-secondary">{t('common.create')} {t('common.class')}</span>
                        </Link>
                        <Link
                          to="/admin/school/learners"
                          className="flex items-center p-4 border border-gray-200 rounded-xl hover:border-primary-300 hover:bg-card-teal transition-all group"
                        >
                          <div className="p-2 rounded-lg bg-card-teal text-primary-500 group-hover:bg-primary-100 transition-colors">
                            <Plus className="h-5 w-5" />
                          </div>
                          <span className="ml-3 text-sm font-semibold text-text-secondary">{t('common.add')} {t('roles.student')}</span>
                        </Link>
                        <Link
                          to="/admin/school/parents"
                          className="flex items-center p-4 border border-gray-200 rounded-xl hover:border-primary-300 hover:bg-card-teal transition-all group"
                        >
                          <div className="p-2 rounded-lg bg-card-teal text-primary-500 group-hover:bg-primary-100 transition-colors">
                            <Plus className="h-5 w-5" />
                          </div>
                          <span className="ml-3 text-sm font-semibold text-text-secondary">{t('common.invite')} {t('roles.parent')}</span>
                        </Link>
                      </div>
                    </div>

                    <div className="card p-6">
                      <h2 className="text-lg font-bold text-text-primary mb-6 font-heading">{t('dashboard.billing')}</h2>
                      <div className="flex items-center justify-between mb-6">
                        <div>
                          <p className="text-sm text-text-secondary">{t('pricing.currentPlan')}</p>
                          <p className="text-xl font-bold text-text-primary capitalize">{profile?.schools?.subscription_plan || 'Basic'}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-text-secondary">{t('common.status')}</p>
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                            profile?.schools?.subscription_status === 'trial'
                              ? 'bg-card-orange text-orange-600 border border-orange-200'
                              : profile?.schools?.subscription_status === 'active'
                              ? 'bg-card-green text-success-600 border border-success-200'
                              : 'bg-red-50 text-danger-600 border border-danger-200'
                          }`}>
                            {profile?.schools?.subscription_status === 'trial' ? t('demo.trial') : 
                             profile?.schools?.subscription_status === 'active' ? t('dashboard.paymentActive') : t('dashboard.accessPaused')}
                          </span>
                        </div>
                      </div>
                      {profile?.schools?.subscription_status === 'trial' && (
                        <div className="bg-card-orange border border-orange-200 rounded-xl p-4">
                          <div className="flex items-center">
                            <TrendingUp className="h-5 w-5 text-orange-500 mr-2" />
                            <p className="text-sm text-orange-700">
                              {t('dashboard.trialEnds', { days: Math.ceil((new Date(profile?.schools?.trial_ends_at) - new Date()) / (1000 * 60 * 60 * 24)) })}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              }
            />
            <Route path="*" element={<Navigate to="/admin/school" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  )
}
